/**
 * FloatingCreateButton Component
 *
 * Floating action button (FAB) for creating new stories.
 * Shows credits remaining for logged-in users.
 */

import type { ReactElement } from 'react';
import type { User } from '@/types/user';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Sparkles } from 'lucide-react';

interface FloatingCreateButtonProps {
  user: User | null;
  onClick?: () => void;
}

const FloatingCreateButton = ({ user, onClick }: FloatingCreateButtonProps): ReactElement => {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      navigate('/create');
    }
  };

  const hasCredits = user && user.credits > 0;

  return (
    <button
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`fixed bottom-20 right-4 md:bottom-8 md:right-8 z-40 flex items-center rounded-full text-white font-bold text-sm transition-all duration-300 hover:scale-105 ${hasCredits ? 'fab-pulse' : ''}`}
      style={{
        gap: isHovered ? '0.5rem' : '0',
        padding: isHovered ? '0.875rem 1.25rem' : '0.875rem',
        background: 'linear-gradient(135deg, var(--st-gradient-primary-end) 0%, var(--st-gradient-primary-start) 100%)',
        fontFamily: 'Quicksand, sans-serif',
        boxShadow: '0 4px 20px rgba(255, 140, 97, 0.4)',
      }}
    >
      {isHovered ? (
        <>
          <Sparkles className="w-5 h-5" />
          <span>Create</span>
          {user && (
            <span
              className="px-1.5 py-0.5 rounded-full text-[10px] font-bold"
              style={{ background: 'rgba(255,255,255,0.2)' }}
            >
              {user.credits}
            </span>
          )}
        </>
      ) : (
        <Plus className="w-6 h-6" />
      )}
    </button>
  );
};

export default FloatingCreateButton;
